import React, {useCallback, useMemo} from 'react';
import InternalTools from 'features/InternalTools/InternalTools';
import ComingSoonProductCard from 'features/products/components/ProductCard/ComingSoonProductCard';
import NewProductCard from 'features/products/components/ProductCard/ProductCard';
import {T_SHIRT_SUBCAT_IDS} from '../utils/constants/categoryConstants';
import {
  checkIsUnitPriceDisplayTestApplicable,
  UNITS_QTY
} from '../utils/unitPriceDisplayTest';
import {getProductCardInsightsData} from './utils/algoliaInsights';
import {buildProductImage} from './utils/imageSourceBuilder';
import {getInitialSelectedColorId} from './utils/product';
import {forceCoverImageForSubcategory} from './utils/productImage';
import productPath from './utils/productPath';

export default function ProductCardRenderer({
  backColor,
  categoryId,
  cid,
  compareHandler,
  compareStyles,
  currentSelectedColorFilters,
  defaultQuoteQty,
  defaultQuoteQtyMap,
  fireSelectProductEvent,
  frontColor,
  hit,
  iframer,
  index,
  internalCatalog,
  minQtyToggleSet,
  productBadges,
  quantity,
  quoteQuantityDirty,
  sendEvent,
  showUnbundledPriceCategoryLevel,
  showUnbundledPriceSiteWideSearch,
  siteWideSearch,
  ydhSettings
}) {
  const handleProductSelect = useCallback(() => {
    // Fire Google Analytics event
    fireSelectProductEvent(hit, index + 1);

    // Fire Algolia conversion event
    sendEvent('click', hit, 'ProductClicked');
  }, [fireSelectProductEvent, hit, index, sendEvent]);

  // Helper function to get filtered product colors - memoized for performance
  const getProductColors = useCallback(
    (product) => {
      const unfilteredColors = product.colors || [];
      let productColors =
        currentSelectedColorFilters.length > 0
          ? unfilteredColors.filter((color) =>
              color.simple_names.some((colorName) =>
                currentSelectedColorFilters.includes(colorName)
              )
            )
          : unfilteredColors;

      if (minQtyToggleSet) {
        productColors = productColors.filter((color) => color.min_qty === 1);
      }

      return productColors;
    },
    [currentSelectedColorFilters, minQtyToggleSet]
  );

  const productColors = getProductColors(hit);

  const filterColorId =
    currentSelectedColorFilters.length > 0 ? productColors[0]?.id : null;

  const productQuoteQty =
    (quoteQuantityDirty && quantity) ||
    defaultQuoteQty ||
    (siteWideSearch && defaultQuoteQtyMap[hit.primary_category_id]);

  const pricingQuantity = checkIsUnitPriceDisplayTestApplicable({
    categoryId,
    defaultUnitPrices: hit.default_unit_prices,
    desiredQty: UNITS_QTY,
    styleType: hit.style_type
  })
    ? UNITS_QTY
    : productQuoteQty || quantity;

  const isTshirtStyle = useMemo(() => {
    return (
      hit.categories?.some((category) =>
        T_SHIRT_SUBCAT_IDS.includes(category.id)
      ) || false
    );
  }, [hit]);

  const showUnbundledPrice = useMemo(() => {
    return (
      showUnbundledPriceCategoryLevel ||
      (showUnbundledPriceSiteWideSearch && isTshirtStyle)
    );
  }, [
    showUnbundledPriceCategoryLevel,
    showUnbundledPriceSiteWideSearch,
    isTshirtStyle
  ]);

  // Common props for all product cards
  const baseProps = {
    key: hit.product_id,
    minQtyToggleSet,
    showUnbundledPrice
  };

  // Common props for ComingSoonProductCard and NewProductCard
  const newCardCommonProps = {
    badges: (productBadges && productBadges[hit.product_id]) || null,
    displayAllSwatches: currentSelectedColorFilters.length !== 0,
    displayImageAsCover: forceCoverImageForSubcategory(hit.sub_category_names),
    getProductImageSrcData: (colorId = null, noMoreLaydowns = false) =>
      buildProductImage({
        categoryId,
        cid,
        hit,
        iframer,
        noMoreLaydowns,
        selectedColorId: colorId,
        siteWideSearch,
        ydhSettings
      }),
    initialSelectedColorId: getInitialSelectedColorId(
      hit,
      siteWideSearch ? hit.primary_category_id : categoryId
    ),
    name: hit.name,
    productColors,
    productPath: productPath(hit)
  };

  // Common props for NewProductCard
  const colorProps = {
    backColor,
    frontColor
  };

  // Render ComingSoonProductCard for preview products
  if (hit.status === 'preview') {
    return (
      <ComingSoonProductCard
        {...baseProps}
        {...newCardCommonProps}
        productId={Number(hit.product_id)}
        containerProps={getProductCardInsightsData(hit)}
        onSelectProduct={handleProductSelect}
      />
    );
  }

  // Always render NewProductCard for all other products
  return (
    <NewProductCard
      {...baseProps}
      {...newCardCommonProps}
      {...colorProps}
      productId={Number(hit.product_id)}
      filterColorId={filterColorId}
      categoryId={Number(categoryId)}
      siteWideSearch={siteWideSearch}
      status={hit.status}
      categoryImages={hit.category_images}
      ratingCount={Number(hit.rating_count)}
      ratingScore={Number(hit.rating_score)}
      generalComments={hit.general_comments}
      sizes={hit.sizes}
      defaultUnitPrices={hit.default_unit_prices}
      minQty={hit.minimum_quantity}
      noQtyLimit={Boolean(hit.no_minimum) || minQtyToggleSet}
      pricingQuantity={pricingQuantity}
      hasSinglesEnabledColors={hit.has_singles_enabled_colors}
      specialty={hit.specialty}
      rushDeliveryTurntimes={hit.rush_delivery_turntimes}
      decorationMethod={hit.decoration_method}
      containerProps={getProductCardInsightsData(hit)}
      onSelectProduct={handleProductSelect}
      hideDetails={Boolean(hit.metadata?.STYLES_TARGET_CATEGORY_PROMOTION)}
      hasDirtyQuoteQuantity={!!quoteQuantityDirty}
      {...(internalCatalog && {
        footerContent: (
          <InternalTools
            colorLimit={hit.color_limit}
            compareStyles={compareStyles}
            compareHandler={compareHandler}
            decorationMethod={hit.decoration_method}
            productName={hit.name}
            productPath={productPath(hit)}
            speciality={hit.specialty}
            styleId={hit.product_id}
          />
        )
      })}
    />
  );
}
